import { useEffect, useState } from "react";
import { cacheBustingReload } from "@/lib/lazyWithRetry";
import { Outlet, useSearchParams, useLocation } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Bell, RefreshCw, Search, Sparkles } from "lucide-react";
import logo from "@/assets/aci-logo-new.jpeg";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppSidebar } from "@/components/AppSidebar";
import { GlobalSearch } from "@/components/GlobalSearch";
import { DashBot } from "@/components/DashBot";
import { FloatingChat } from "@/components/FloatingChat";
import { NotificationBell } from "@/components/NotificationBell";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSectionAccess } from "@/hooks/useSectionAccess";
import { Link } from "react-router-dom";
import { SessionTimeoutGuard } from "@/components/SessionTimeoutGuard";

/** Blocks pages in sections the user has not been granted. */
const SectionGate = ({ path, children }: { path: string; children: React.ReactNode }) => {
  const { canPath, isLoading } = useSectionAccess();
  if (isLoading || canPath(path)) return <>{children}</>;
  return (
    <div className="flex items-center justify-center h-full p-8">
      <div className="max-w-md text-center space-y-3">
        <h2 className="text-xl font-semibold text-foreground">No access to this section</h2>
        <p className="text-sm text-muted-foreground">Ask an admin to grant you access if you need it.</p>
        <Button asChild variant="outline"><Link to="/">Back to Activity</Link></Button>
      </div>
    </div>
  );
};

/** Shown when a header panel fails: clickable, reloads with a fresh copy. */
const IconRecoveryButton = ({ label, icon: Icon }: { label: string; icon: typeof Search }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 relative text-muted-foreground"
        aria-label={`${label} — click to reload`}
        onClick={() => cacheBustingReload(true)}
      >
        <Icon className="h-4 w-4" />
        <RefreshCw className="h-2.5 w-2.5 absolute translate-x-2 translate-y-2" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label} didn't load — click to reload the page</TooltipContent>
  </Tooltip>
);

const AppLayout = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [openMessaging, setOpenMessaging] = useState(false);
  const [initialChannelId, setInitialChannelId] = useState<string | null>(null);
  const [initialDmPartnerId, setInitialDmPartnerId] = useState<string | null>(null);
  const [initialDmPartnerName, setInitialDmPartnerName] = useState<string | null>(null);

  // Read raw param values so the effect depends on primitive strings
  // instead of the unstable URLSearchParams object identity (which can
  // change on every render and cause a render loop when combined with
  // setSearchParams).
  const openMessagingParam = searchParams.get("openMessaging");
  const messagingParam = searchParams.get("messaging");
  const channelIdParam = searchParams.get("channelId") || searchParams.get("channel");
  const dmIdParam = searchParams.get("dmId") || searchParams.get("dm");
  const dmNameParam = searchParams.get("dmName");

  // Handle query params for opening messaging from notifications and global search
  useEffect(() => {
    const shouldOpenMessaging = openMessagingParam === "true" || messagingParam === "true";
    if (!shouldOpenMessaging) return;

    setOpenMessaging(true);
    if (channelIdParam) {
      setInitialChannelId(channelIdParam);
    }
    if (dmIdParam) {
      setInitialDmPartnerId(dmIdParam);
      let decodedName: string | null = null;
      if (dmNameParam) {
        try {
          decodedName = decodeURIComponent(dmNameParam);
        } catch {
          decodedName = dmNameParam;
        }
      }
      setInitialDmPartnerName(decodedName);
    }

    // Clean up query params on a fresh copy so we don't mutate the
    // hook's internal object (mutation + setSearchParams can produce
    // an unstable effect identity in some routers).
    const next = new URLSearchParams(searchParams);
    let changed = false;
    for (const key of [
      "openMessaging",
      "messaging",
      "channelId",
      "channel",
      "dmId",
      "dm",
      "dmName",
    ]) {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMessagingParam, messagingParam, channelIdParam, dmIdParam, dmNameParam]);

  const handleMessagingClose = () => {
    setOpenMessaging(false);
    setInitialChannelId(null);
    setInitialDmPartnerId(null);
    setInitialDmPartnerName(null);
  };

  return (
    <SidebarProvider>
      <SessionTimeoutGuard />
      <div className="h-screen flex w-full overflow-hidden">
        <ErrorBoundary label="AppSidebar" silent fallback={null}>
          <AppSidebar />
        </ErrorBoundary>
        <div className="flex-1 flex flex-col min-w-0 h-full">
          {/* Global Header - sticky */}
          <header className="h-14 border-b bg-background/95 backdrop-blur shrink-0 flex items-center gap-4 px-4 z-10">
            <SidebarTrigger className="-ml-1" />
            <div className="flex items-center gap-2">
              <img src={logo} alt="African Creative Investments" className="h-8 w-auto mix-blend-multiply dark:mix-blend-screen dark:invert" style={{ filter: 'grayscale(1)' }} />
              <span className="text-sm font-semibold text-foreground hidden sm:inline">BusinessOS</span>
            </div>
            <div className="flex items-center gap-2">
              <ErrorBoundary label="DashBot" silent fallback={<IconRecoveryButton label="dashBOT" icon={Sparkles} />}>
                <DashBot />
              </ErrorBoundary>
              <ErrorBoundary label="NotificationBell" silent fallback={<IconRecoveryButton label="Notifications" icon={Bell} />}>
                <NotificationBell />
              </ErrorBoundary>
              <ErrorBoundary label="GlobalSearch" silent fallback={<IconRecoveryButton label="Search" icon={Search} />}>
                <GlobalSearch />
              </ErrorBoundary>
            </div>
            <div className="ml-auto" />
          </header>
          
          {/* Main Content Area */}
          <main className="flex-1 overflow-auto">
            <ErrorBoundary key={location.pathname} label={`RoutePage:${location.pathname}`}>
              <SectionGate path={location.pathname}>
                <Outlet />
              </SectionGate>
            </ErrorBoundary>
          </main>
        </div>
        
        {/* Persistent Floating Chat */}
        <ErrorBoundary label="FloatingChat" silent fallback={null}>
          <FloatingChat
            externalOpen={openMessaging}
            onExternalOpenChange={(open) => {
              if (!open) handleMessagingClose();
              else setOpenMessaging(true);
            }}
            initialChannelId={initialChannelId}
            initialDmPartnerId={initialDmPartnerId}
            initialDmPartnerName={initialDmPartnerName}
          />
        </ErrorBoundary>
      </div>
    </SidebarProvider>
  );
};

export { AppLayout };
